/**
 * Regional UI copy (CLA-1829): AU vs US words and spelling, picked by the
 * realm (`pageRealm()`), never the device locale. A US clinician's laptop may
 * well be set to en-AU, and the reverse. The realm is the signed-in one in the
 * app; on a signed-out public page (booking, manage-booking, consent, the
 * portal) it is the realm of the deployment that answered the page's calls.
 *
 *     const t = useRegionalCopy()
 *     t('analyse')     // "analyse" / "analyze"
 *     t('Authorise')   // "Authorise" / "Authorize" (capital key → capital value)
 *     t('clinic')      // "clinic" / "practice"
 *
 *     regionalText('Invite someone to your clinic')
 *                      // "… to your clinic" / "… to your practice"
 *     regionalText('Move {name} to another clinic', { name: clinic.name })
 *                      // values are never converted
 *
 * `regionalText` is for whole UI strings: it swaps only the words in
 * REGIONAL_TEXT_WORDS (clinic → practice and the reviewed AU → US
 * spellings), whole words, keeping case. Wrapping a string is the author
 * saying "this is copy, and every word in it is ours to localise"; nothing
 * outside a wrapped string is ever touched. `regional-copy.guard.test.ts`
 * fails CI on an unwrapped user-facing string with one of these words.
 *
 * Mirrors the help system's tables in claro-elixir so the app and the help
 * articles say the same thing: `Claro.Help.Regional` @terms (words for the
 * same thing) and `Claro.Help.Spelling` (AU → US spelling). Keep entries that
 * exist on both sides identical. Like the server, this is a reviewed word list,
 * never an "-ise → -ize" rule: that rule breaks advise, exercise, otherwise.
 *
 * Concepts that exist in only one region (Medicare, AHPRA, GFE, NPI) are not
 * translations: gate that UI on the realm instead. The `provider_number` and
 * `registration` entries are the exception — the same field, labelled the
 * way each country names it.
 *
 * Jurisdictional DATA (address labels, tax, timezones, schedules) stays in
 * lib/jurisdiction.ts, which the server can drive. This file is words only.
 *
 * Like realm.ts this is a module read, not React state: the realm only
 * changes across a full-page navigation (the region switcher).
 */

import type { Realm } from '@/lib/realm'
import { pageRealm } from '@/lib/served-realm'

type Entry = Record<Realm, string>

export const REGIONAL_COPY = {
  // ── Same thing, different word (Claro.Help.Regional @terms) ────────────
  clinic: { au: 'clinic', us: 'practice' },
  clinics: { au: 'clinics', us: 'practices' },
  mobile: { au: 'mobile', us: 'cell' },
  postcode: { au: 'postcode', us: 'ZIP code' },
  registration: { au: 'AHPRA registration', us: 'state license' },
  emergency_number: { au: '000', us: '911' },
  // App-only terms
  provider_number: { au: 'provider number', us: 'NPI' },
  indemnity_insurance: { au: 'indemnity insurance', us: 'malpractice insurance' },

  // ── Spelling (Claro.Help.Spelling) ─────────────────────────────────────
  analyse: { au: 'analyse', us: 'analyze' },
  analysing: { au: 'analysing', us: 'analyzing' },
  authorise: { au: 'authorise', us: 'authorize' },
  authorised: { au: 'authorised', us: 'authorized' },
  authorising: { au: 'authorising', us: 'authorizing' },
  customise: { au: 'customise', us: 'customize' },
  customised: { au: 'customised', us: 'customized' },
  organisation: { au: 'organisation', us: 'organization' },
  organisations: { au: 'organisations', us: 'organizations' },
  colour: { au: 'colour', us: 'color' },
  cancelled: { au: 'cancelled', us: 'canceled' },
} as const satisfies Record<string, Entry>

export type RegionalCopyKey = keyof typeof REGIONAL_COPY

/** A key, optionally with a capital first letter to get a capitalised value. */
export type RegionalCopyLookup = RegionalCopyKey | Capitalize<RegionalCopyKey>

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * The realm's word for `key`. A capital first letter in the key capitalises
 * the value (`'Clinic'` → "Practice"), the same convention as the help
 * articles' `{{term:Clinic}}`. Values that start with a capital or a digit
 * ("NPI", "ZIP code", "911") are unchanged either way.
 */
export function regionalCopy(key: RegionalCopyLookup, realm: Realm = pageRealm()): string {
  const lower = (key.charAt(0).toLowerCase() + key.slice(1)) as RegionalCopyKey
  const value = REGIONAL_COPY[lower][realm]
  return key === lower ? value : capitalise(value)
}

// ── Whole strings ──────────────────────────────────────────────────────

/**
 * Every word `regionalText` changes for the US, AU → US, lower case (case is
 * kept: "Clinic" → "Practice", "CLINIC" → "PRACTICE"). A reviewed list, like
 * the server's `Claro.Help.Spelling`, never a rule. Not here on purpose:
 * "cancellation" (US spelling too), "mobile" and "postcode" (a "mobile app"
 * is not a cell app: use `regionalCopy('mobile')` where the phone is meant).
 */
export const REGIONAL_TEXT_WORDS: Readonly<Record<string, string>> = {
  clinic: 'practice',
  clinics: 'practices',
  analyse: 'analyze',
  analysed: 'analyzed',
  analysing: 'analyzing',
  authorise: 'authorize',
  authorised: 'authorized',
  authorises: 'authorizes',
  authorising: 'authorizing',
  authorisation: 'authorization',
  behaviour: 'behavior',
  behaviours: 'behaviors',
  cancelled: 'canceled',
  cancelling: 'canceling',
  catalogue: 'catalog',
  catalogues: 'catalogs',
  centre: 'center',
  centres: 'centers',
  colour: 'color',
  colours: 'colors',
  customise: 'customize',
  customised: 'customized',
  customises: 'customizes',
  customising: 'customizing',
  customisation: 'customization',
  enrol: 'enroll',
  enrols: 'enrolls',
  enrolment: 'enrollment',
  enrolments: 'enrollments',
  favourite: 'favorite',
  favourites: 'favorites',
  finalise: 'finalize',
  finalised: 'finalized',
  finalises: 'finalizes',
  finalising: 'finalizing',
  fulfilment: 'fulfillment',
  grey: 'gray',
  licence: 'license',
  licences: 'licenses',
  organisation: 'organization',
  organisations: 'organizations',
  programme: 'program',
  programmes: 'programs',
  summarise: 'summarize',
  summarised: 'summarized',
  summarises: 'summarizes',
  summarising: 'summarizing',
}

/**
 * A REGIONAL_TEXT_WORDS word as a whole word. Not part of a domain or an
 * address ("claro.clinic", "help@claro.clinic", "/manage/clinic/"), and not
 * the company name "Fresh Clinics".
 */
export const REGIONAL_WORD_RE = new RegExp(
  `(?<![.@/\\w])(?<!Fresh )(${Object.keys(REGIONAL_TEXT_WORDS).join('|')})(?![\\w/@]|\\.\\w)`,
  'gi',
)

/** True when `text` has a word `regionalText` would change for the US. */
export function hasRegionalWord(text: string): boolean {
  REGIONAL_WORD_RE.lastIndex = 0
  return REGIONAL_WORD_RE.test(text)
}

function matchCase(original: string, us: string): string {
  if (original === original.toUpperCase()) return us.toUpperCase()
  if (original[0] === original[0].toUpperCase()) return capitalise(us)
  return us
}

/**
 * `text` in the realm's words and spelling: unchanged for AU; for the US each
 * REGIONAL_TEXT_WORDS word is swapped, case kept. Call it where the string is
 * rendered (not at module load: the realm lands after sign-in).
 */
export function regionalText(
  text: string,
  values: Readonly<Record<string, string | number>> = {},
  realm: Realm = pageRealm(),
): string {
  // Values go in after the words are swapped, so a clinic called "Skin
  // Clinic Sydney" or a typed reason stays exactly as it was entered.
  return toRealm(text, realm).replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? String(values[key]) : whole,
  )
}

function toRealm(text: string, realm: Realm): string {
  if (realm === 'au') return text
  return text.replace(REGIONAL_WORD_RE, (word) => matchCase(word, REGIONAL_TEXT_WORDS[word.toLowerCase()]))
}

/** Hook form of `regionalCopy`, bound to the page's realm. */
export function useRegionalCopy(): (key: RegionalCopyLookup) => string {
  const realm = pageRealm()
  return (key) => regionalCopy(key, realm)
}
